import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEnv } from '@/src/application/hooks/util/useEnv';
import configData from '@/src/config.json';
import { APIService } from '@/src/infrastructure/Protocol/APIService';

import { CampaignsService } from './CampaignsService';
import { ICampaignsService } from './ICampaignsService';
import { StubCampaignsService } from './StubCampaignsService';

export const useCampaignsService = () => {
  const { env } = useEnv();

  const service = useMemo(() => {
    if (env !== 'storybook') {
      const { URL_EMA_APP_V2, JWT_DEV_TOKEN } = configData.endpoints[env];
      const jwt =
        JWT_DEV_TOKEN !== ''
          ? JWT_DEV_TOKEN
          : (window as Window & typeof globalThis & { __JWTToken: string }).__JWTToken;

      return CampaignsService.create(new APIService(URL_EMA_APP_V2, jwt));
    } else {
      return StubCampaignsService.create();
    }
  }, [env]);

  return service;
};

export const useCampaignsGetterService = <T extends keyof ICampaignsService>(
  method: T,
  ...p: Parameters<ICampaignsService[T]>
): {
  data: Awaited<ReturnType<ICampaignsService[T]>> | undefined;
  error: boolean;
  loading: boolean;
  reFetchData: () => void;
} => {
  const service = useCampaignsService();
  const [data, setData] = useState<Awaited<ReturnType<ICampaignsService[T]>>>();
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const params = JSON.stringify(p);

  const fetchData = useCallback(async () => {
    if (!service) throw new Error(`EM_Error The ${typeof service} does not exist`);

    const fn: unknown = service[method];

    if (typeof fn !== 'function')
      throw new Error(`Method '${method}' does not exist on service '${typeof service}'`);

    try {
      setLoading(true);
      const result = (await fn.call(
        service,
        ...(JSON.parse(params) as Parameters<ICampaignsService[T]>)
      )) as Awaited<ReturnType<ICampaignsService[T]>>;

      if (result?.statusOK) throw new Error('Error Campaigns Service');

      setData(result);
      setLoading(false);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [service, method, params]);

  const reFetchData = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
  }, [method, service, params, fetchData]);

  return { data, error, loading, reFetchData };
};
